import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, KeyRound, Plus, RefreshCw, Trash2, UserCheck, UserX, Users, Copy } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('spare_admin_token')}` });

export default function SpareAdmin({ admin, onExit }) {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', name: '', company: '' });
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPw, setResetPw] = useState('');

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/spares/users`, { headers: authHeaders() });
      setCustomers(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load customers');
      if (e.response?.status === 401) onExit();
    }
    setLoading(false);
  }, [onExit]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  const genPassword = async (setter) => {
    try {
      const { data } = await axios.post(`${API}/api/spares/users/generate-password`, {}, { headers: authHeaders() });
      setter(data.password);
    } catch { toast.error('Could not generate password'); }
  };

  const createCustomer = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/api/spares/users`, form, { headers: authHeaders() });
      toast.success(`Customer ${form.email} created`);
      setShowCreate(false);
      setForm({ email: '', password: '', name: '', company: '' });
      fetchCustomers();
    } catch (e2) { toast.error(e2.response?.data?.detail || 'Create failed'); }
  };

  const toggleActive = async (c) => {
    try {
      await axios.patch(`${API}/api/spares/users/${c.id}`, { active: !c.active }, { headers: authHeaders() });
      toast.success(`${c.email} ${c.active ? 'deactivated' : 'activated'}`);
      fetchCustomers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };

  const deleteCustomer = async (c) => {
    if (!window.confirm(`Delete customer account ${c.email}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/api/spares/users/${c.id}`, { headers: authHeaders() });
      toast.success(`${c.email} deleted`);
      fetchCustomers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const doReset = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/api/spares/users/${resetTarget.id}/reset-password`, { new_password: resetPw }, { headers: authHeaders() });
      toast.success(`Password updated for ${resetTarget.email}`);
      setResetTarget(null); setResetPw('');
    } catch (e2) { toast.error(e2.response?.data?.detail || 'Reset failed'); }
  };

  const copyText = (t) => { navigator.clipboard?.writeText(t); toast.success('Copied'); };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(160deg, #0b1524 0%, #101d31 100%)' }} data-testid="spare-admin-page">
      <header className="flex items-center justify-between px-6 py-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <img src="/images/kiel-logo.png" alt="Kiel" className="h-8 object-contain" />
          <div>
            <h1 className="text-white text-sm font-bold flex items-center gap-2"><Users className="w-4 h-4 text-amber-400" /> Spare Parts — Customer Accounts</h1>
            <p className="text-slate-500 text-[10px]">Signed in as {admin?.email} (Administrator)</p>
          </div>
        </div>
        <button onClick={onExit}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/10 text-slate-300 text-xs font-semibold hover:bg-white/20 border border-white/10"
          data-testid="spare-admin-exit-btn">
          <ArrowLeft className="w-3.5 h-3.5" /> Exit Administration
        </button>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-4">
          <div className="text-slate-400 text-xs">{customers.length} customer account{customers.length !== 1 && 's'}</div>
          <div className="flex items-center gap-2">
            <button onClick={fetchCustomers} className="p-2 rounded-lg bg-white/5 border border-white/10 text-slate-400 hover:text-white" data-testid="spare-admin-refresh">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={() => setShowCreate(s => !s)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-cyan-500 text-slate-900 text-xs font-bold hover:bg-cyan-400"
              data-testid="spare-admin-add-btn">
              <Plus className="w-3.5 h-3.5" /> New Customer
            </button>
          </div>
        </div>

        {showCreate && (
          <form onSubmit={createCustomer} className="bg-white/[0.05] border border-white/10 rounded-xl p-4 mb-5 grid sm:grid-cols-2 gap-3" data-testid="spare-admin-create-form">
            <input type="email" required placeholder="customer@company.com" value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400/60"
              data-testid="create-email-input" />
            <div className="flex gap-2">
              <input type="text" required minLength={8} placeholder="Password (min 8 chars)" value={form.password}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400/60"
                data-testid="create-password-input" />
              <button type="button" onClick={() => genPassword(pw => setForm(f => ({ ...f, password: pw })))}
                className="px-2.5 rounded-lg bg-white/10 border border-white/10 text-slate-300 text-[10px] font-semibold hover:bg-white/20"
                title="Generate strong password" data-testid="create-genpw-btn">
                <KeyRound className="w-3.5 h-3.5" />
              </button>
            </div>
            <input type="text" placeholder="Contact name (optional)" value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400/60" />
            <input type="text" placeholder="Company (optional)" value={form.company}
              onChange={e => setForm(f => ({ ...f, company: e.target.value }))}
              className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400/60" />
            <div className="sm:col-span-2 flex items-center justify-between">
              <span className="text-[10px] text-slate-500">Share the password securely with the customer — it is stored encrypted and cannot be viewed later.</span>
              <div className="flex gap-2">
                {form.password && (
                  <button type="button" onClick={() => copyText(form.password)}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white/10 text-slate-300 text-xs font-semibold hover:bg-white/20">
                    <Copy className="w-3 h-3" /> Copy password
                  </button>
                )}
                <button type="submit" className="px-4 py-1.5 rounded-lg bg-emerald-500 text-slate-900 text-xs font-bold hover:bg-emerald-400" data-testid="create-submit-btn">
                  Create Account
                </button>
              </div>
            </div>
          </form>
        )}

        <div className="bg-white/[0.04] border border-white/10 rounded-xl overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-slate-500 uppercase text-[9px] tracking-wider border-b border-white/10">
                <th className="px-4 py-2.5 text-left">Email</th>
                <th className="px-4 py-2.5 text-left">Name / Company</th>
                <th className="px-4 py-2.5 text-left">Status</th>
                <th className="px-4 py-2.5 text-left">Last Login</th>
                <th className="px-4 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={5} className="text-center py-8 text-slate-500">Loading…</td></tr>}
              {!loading && customers.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-slate-500 italic">No customer accounts yet.</td></tr>}
              {!loading && customers.map(c => (
                <tr key={c.id} className="border-b border-white/5 hover:bg-white/[0.03]" data-testid={`customer-row-${c.email}`}>
                  <td className="px-4 py-2.5 text-slate-200 font-mono">{c.email}</td>
                  <td className="px-4 py-2.5 text-slate-400">{c.name || '—'}{c.company ? ` · ${c.company}` : ''}</td>
                  <td className="px-4 py-2.5">
                    <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${c.active ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' : 'bg-red-500/15 text-red-400 border border-red-500/30'}`}>
                      {c.active ? 'ACTIVE' : 'DISABLED'}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-slate-500">{c.last_login ? c.last_login.slice(0, 16).replace('T', ' ') : 'Never'}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => { setResetTarget(c); setResetPw(''); }}
                        className="p-1.5 rounded-md hover:bg-white/10 text-amber-400" title="Reset password" data-testid={`reset-pw-${c.email}`}>
                        <KeyRound className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => toggleActive(c)}
                        className={`p-1.5 rounded-md hover:bg-white/10 ${c.active ? 'text-slate-400' : 'text-emerald-400'}`}
                        title={c.active ? 'Deactivate' : 'Activate'} data-testid={`toggle-active-${c.email}`}>
                        {c.active ? <UserX className="w-3.5 h-3.5" /> : <UserCheck className="w-3.5 h-3.5" />}
                      </button>
                      <button onClick={() => deleteCustomer(c)}
                        className="p-1.5 rounded-md hover:bg-white/10 text-red-400" title="Delete account" data-testid={`delete-${c.email}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>

      {resetTarget && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setResetTarget(null)}>
          <form onSubmit={doReset} onClick={e => e.stopPropagation()}
            className="bg-slate-900 border border-white/10 rounded-xl p-5 w-full max-w-sm" data-testid="reset-pw-modal">
            <h3 className="text-white text-sm font-bold mb-1">Reset password</h3>
            <p className="text-slate-500 text-[10px] mb-4">{resetTarget.email}</p>
            <div className="flex gap-2 mb-4">
              <input type="text" required minLength={8} value={resetPw} onChange={e => setResetPw(e.target.value)}
                placeholder="New password (min 8 chars)"
                className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400/60"
                data-testid="reset-pw-input" autoFocus />
              <button type="button" onClick={() => genPassword(setResetPw)}
                className="px-2.5 rounded-lg bg-white/10 border border-white/10 text-slate-300 hover:bg-white/20" title="Generate">
                <KeyRound className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="flex justify-end gap-2">
              {resetPw && (
                <button type="button" onClick={() => copyText(resetPw)}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white/10 text-slate-300 text-xs font-semibold hover:bg-white/20">
                  <Copy className="w-3 h-3" /> Copy
                </button>
              )}
              <button type="button" onClick={() => setResetTarget(null)} className="px-3 py-1.5 rounded-lg bg-white/10 text-slate-300 text-xs font-semibold hover:bg-white/20">Cancel</button>
              <button type="submit" className="px-4 py-1.5 rounded-lg bg-amber-500 text-slate-900 text-xs font-bold hover:bg-amber-400" data-testid="reset-pw-submit">Update</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
